import React, { useState } from 'react';
import cn from 'classnames';

import SalonModal from 'components/dialog/SalonModal.component';
import Button from 'components/form/Button.component';
import ToggleSwitch from 'components/form/ToggleSwitch.component';
import ToggleSwitchPanel from 'components/form/ToggleSwitchPanel.component';
import Icon from 'components/generic/Icon.component';
import useTip from 'hooks/useTip.hook';
import { useGilt, useTranslation } from 'providers/GILT.provider';
import { useTheme } from 'providers/Theme.provider';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import alwaysNoop from 'utils/functions/nullary/alwaysNoop.util';

import InputField from 'onboarding/common/InputField';

import CN from './AddRuleModal.module.scss';

const APPLIED_TO = {
  services: 'services',
  products: 'products',
  all: 'products_and_services',
};

export const AddRuleModal = ({ rule, isOpen, onClose, onConfirm, isDisabled, hasError }) => {
  const { t } = useTranslation();
  const { isSalonInUS } = useGilt();
  const { isDarkMode } = useTheme();

  const isEditModal = fromAnyToBool(rule);
  const isAmountRule = fromAnyToBool(rule?.amount);
  const isTaxRule = rule?.amount === undefined;
  const isAutoApplyDefault = fromAnyToBool(rule?.is_default || true);
  const isAutoApplyExistingDefault = fromAnyToBool(rule?.apply_to_existing || true);
  const defaultAmount = isAmountRule ? rule?.amount : rule?.percentage;

  const { val: isTax, yay: setIsTax, nay: setIsFee } = useTip(isTaxRule && isSalonInUS);
  const { val: isAmount, yay: setIsAmount, nay: setIsPercentage } = useTip(isAmountRule);
  const { val: isAutoApply, yay: setIsAutoApply, nay: setIsNotAutoApply } = useTip(isAutoApplyDefault);
  const { val: isAutoApplyExisting, yay: setIsAutoApplyExisting, nay: setIsNotAutoApplyExisting } = useTip(isAutoApplyExistingDefault);

  const [name, setName] = useState(rule?.name ?? '');
  const [errors, setErrors] = useState({
    amount: '',
    name: '',
  });
  const [amount, setAmount] = useState(defaultAmount ?? '');
  const [appliedTo, setAppliedTo] = useState(rule?.applied_to || APPLIED_TO.services);

  const selectTaxType = () => {
    setIsTax();
    setIsPercentage();
    !!amount && validateAmount({ isFlatAmount: false });
  };

  const selectFeeType = () => {
    setIsFee();
    setIsAmount();
    !!amount && validateAmount({ isFlatAmount: true });
  };

  const selectAmount = () => {
    setIsAmount();
    !!amount && validateAmount({ isFlatAmount: true });
  };

  const selectPercentage = () => {
    setIsPercentage();
    !!amount && validateAmount({ isFlatAmount: false });
  };

  const validateAmount = ({ isFlatAmount }) => {
    if (!fromAnyToBool(amount)) {
      setErrors((prev) => ({
        ...prev,
        amount: t('composite.addRuleModal.amountErrorMessage'),
      }));
      return { isValid: false };
    }
    if (isFlatAmount && parseFloat(amount) <= 0) {
      setErrors((prev) => ({
        ...prev,
        amount: t('composite.addRuleModal.flatAmountErrorMessage'),
      }));
      return { isValid: false };
    }
    if (!isFlatAmount && (parseFloat(amount) < 1 || parseFloat(amount) > 100)) {
      setErrors((prev) => ({
        ...prev,
        amount: t('composite.addRuleModal.percentageAmountErrorMessage'),
      }));
      return { isValid: false };
    }
    setErrors((prev) => ({
      ...prev,
      amount: '',
    }));
    return { isValid: true };
  };

  const validateName = () => {
    if (fromAnyToTrimmed(name).length <= 2) {
      setErrors((prev) => ({
        ...prev,
        name: t('composite.addRuleModal.nameErrorMessage'),
      }));
      return { isValid: false };
    }
    setErrors((prev) => ({
      ...prev,
      name: '',
    }));
    return { isValid: true };
  };

  const confirm = () => {
    const amountValidation = validateAmount({ isFlatAmount: isAmount });
    const nameValidation = validateName();

    if (!amountValidation.isValid || !nameValidation.isValid) {
      return;
    }

    onConfirm?.({ name, amount, appliedTo, isTax, isAmount, isDefault: isAutoApply, isDefaultExisting: isAutoApplyExisting });
  };

  const amountPlaceholder = isAmount ? t('composite.addRuleModal.amountPlaceholder') : t('composite.addRuleModal.percentPlaceholder');

  const autoApplyToMap = {
    new: {
      services: t('composite.addRuleModal.autoApplyServicesLabel'),
      products: t('composite.addRuleModal.autoApplyProductsLabel'),
      products_and_services: t('composite.addRuleModal.autoApplyAllLabel'),
    },
    existing: {
      services: t('composite.addRuleModal.autoApplyExistingServicesLabel'),
      products: t('composite.addRuleModal.autoApplyExistingProductsLabel'),
      products_and_services: t('composite.addRuleModal.autoApplyExistingAllLabel'),
    },
  };

  return (
    <SalonModal tag="AddRuleModal__modal" isOpen={isOpen} withFooter customClass={cn(CN.addRuleModal, isDarkMode && CN.dark)} onClose={onClose}>
      <SalonModal.Header>
        <div className={CN.ruleModalHeader}>
          <span className={CN.title}>
            {!isEditModal && t('composite.addRuleModal.title')}
            {isEditModal && t(`composite.addRuleModal.${isTax ? 'editTaxTitle' : 'editFeeTitle'}`)}
          </span>
          <Button variant="empty" onClick={onClose}>
            <Icon variant="close" variantInvert />
          </Button>
        </div>
      </SalonModal.Header>
      <SalonModal.Body>
        <div className={CN.contentModal}>
          {isSalonInUS && !isEditModal && (
            <div className={CN.fieldContainer}>
              <label className={CN.label} htmlFor="type">
                {t('composite.addRuleModal.typeLabel')}
              </label>
              <div className={CN.typeContainer}>
                <ToggleSwitchPanel lightOnDark>
                  <ToggleSwitch lightOnDark xSpace selected={isTax} onClick={selectTaxType} name={t('composite.addRuleModal.taxToggle')} size="normal" />
                  <ToggleSwitch lightOnDark xSpace selected={!isTax} onClick={selectFeeType} name={t('composite.addRuleModal.feeToggle')} size="normal" />
                </ToggleSwitchPanel>
              </div>
            </div>
          )}
          <div className={CN.row}>
            <div className={CN.fieldContainer}>
              <label className={CN.label} htmlFor="name">
                {t('composite.addRuleModal.nameLabel')}
              </label>
              <InputField
                lightOnDark
                data={{
                  id: 'name',
                  inputValue: name,
                  placeholder: t('composite.addRuleModal.namePlaceholder'),
                  type: 'text',
                }}
                onChangeHandler={(value) => setName(value)}
                onBlurHandler={validateName}
              />
              <div className={CN.error}>{errors?.name}</div>
            </div>

            <div className={CN.fieldContainer}>
              <label className={CN.label} htmlFor="amount">
                {t('composite.addRuleModal.amountLabel')}
              </label>
              <div className={CN.amountContainer}>
                <div className={CN.amountInputContainer}>
                  <InputField
                    lightOnDark
                    data={{
                      id: 'amount',
                      inputValue: amount,
                      placeholder: amountPlaceholder,
                      type: 'number',
                    }}
                    onChangeHandler={(value) => setAmount(value)}
                    onBlurHandler={() => validateAmount({ isFlatAmount: isAmount })}
                  />
                  <div className={CN.error}>{errors?.amount}</div>
                </div>
                {!isTax ? (
                  <div className={CN.typeContainer}>
                    <ToggleSwitchPanel lightOnDark>
                      <ToggleSwitch lightOnDark xSpace selected={isAmount} onClick={selectAmount} name={t('composite.addRuleModal.amountToggle')} size="normal" />
                      <ToggleSwitch lightOnDark xSpace selected={!isAmount} onClick={selectPercentage} name={t('composite.addRuleModal.percentToggle')} size="normal" />
                    </ToggleSwitchPanel>
                  </div>
                ) : (
                  <div className={CN.typeContainer}>
                    <ToggleSwitch lightOnDark xSpace onClick={alwaysNoop} name={t('composite.addRuleModal.percentToggle')} size="normal" />
                  </div>
                )}
              </div>
            </div>
          </div>
          {!isEditModal && (
            <>
              <div className={CN.fieldContainer}>
                <label className={CN.label} htmlFor="appliesTo">
                  {t('composite.addRuleModal.appliesToLabel')}
                </label>
                <div className={CN.appliesToContainer}>
                  <ToggleSwitch
                    lightOnDark
                    tag="AddRuleModal__appliesTo--services"
                    selected={appliedTo === APPLIED_TO.services}
                    onClick={() => setAppliedTo(APPLIED_TO.services)}
                    name={t('composite.addRuleModal.appliesToServices')}
                    size="small"
                  >
                    <Icon variant="radioCheck" selected={appliedTo === APPLIED_TO.services} width="lg" />
                  </ToggleSwitch>
                  <ToggleSwitch
                    lightOnDark
                    tag="AddRuleModal__appliesTo--products"
                    selected={appliedTo === APPLIED_TO.products}
                    onClick={() => setAppliedTo(APPLIED_TO.products)}
                    name={t('composite.addRuleModal.appliesToProducts')}
                    size="small"
                  >
                    <Icon variant="radioCheck" selected={appliedTo === APPLIED_TO.products} width="lg" />
                  </ToggleSwitch>
                  <ToggleSwitch
                    lightOnDark
                    tag="AddRuleModal__appliesTo--products_and_services"
                    selected={appliedTo === APPLIED_TO.all}
                    onClick={() => setAppliedTo(APPLIED_TO.all)}
                    name={t('composite.addRuleModal.appliesToProductsAndServices')}
                    size="small"
                  >
                    <Icon variant="radioCheck" selected={appliedTo === APPLIED_TO.all} width="lg" />
                  </ToggleSwitch>
                </div>
              </div>
              <div className={CN.fieldContainer}>
                <label className={CN.label} htmlFor="autoApply">
                  {autoApplyToMap.new[appliedTo]}
                </label>
                <div className={CN.typeContainer}>
                  <ToggleSwitchPanel lightOnDark>
                    <ToggleSwitch lightOnDark selected={isAutoApply} onClick={setIsAutoApply} name={t('composite.addRuleModal.autoApplyToggle')} size="normal" />
                    <ToggleSwitch lightOnDark selected={!isAutoApply} onClick={setIsNotAutoApply} name={t('composite.addRuleModal.notAutoApplyToggle')} size="normal" />
                  </ToggleSwitchPanel>
                </div>
              </div>

              <div className={CN.fieldContainer}>
                <label className={CN.label} htmlFor="autoApplyExisting">
                  {autoApplyToMap.existing[appliedTo]}
                </label>
                <div className={CN.typeContainer}>
                  <ToggleSwitchPanel lightOnDark>
                    <ToggleSwitch lightOnDark selected={isAutoApplyExisting} onClick={setIsAutoApplyExisting} name={t('composite.addRuleModal.autoApplyExistingToggle')} size="normal" />
                    <ToggleSwitch lightOnDark selected={!isAutoApplyExisting} onClick={setIsNotAutoApplyExisting} name={t('composite.addRuleModal.notAutoApplyExistingToggle')} size="normal" />
                  </ToggleSwitchPanel>
                </div>
              </div>
            </>
          )}
          {hasError && <div className={CN.error}>{t('composite.addRuleModal.errorMessage')}</div>}
        </div>
      </SalonModal.Body>
      <SalonModal.Footer className={CN.footer}>
        <Button variant="outlineForce" onClick={onClose}>
          {t('composite.addRuleModal.cancelButton')}
        </Button>
        <Button variant="primaryForce" onClick={confirm} isDisabled={isDisabled}>
          {t('composite.addRuleModal.confirmButton')}
        </Button>
      </SalonModal.Footer>
    </SalonModal>
  );
};
